<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="loadError" class="dw-empty">Could not load vehicle maintenance data.</div>
    <!-- one all-clear when both halves are empty. two headings each
         followed by its own "nothing here" line said the same thing twice
         and filled the card with the absence of news. -->
    <template v-else-if="!notReadyDepartures.length && !oosRows.length">
      <div class="dw-empty">
        Every vehicle departing in the next {{ departureWindowHours }} hours is ready, and none are out of service.
      </div>
    </template>
    <template v-else>
      <div class="dw-section-label">Not Ready, Departing Within {{ departureWindowHours }} Hours</div>
      <div v-if="!notReadyDepartures.length" class="dw-empty" style="margin-bottom: 10px">All clear.</div>
      <div v-else class="dw-table-wrap" style="margin-bottom: 14px">
        <table class="dw-table">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Wash Status</th>
              <th>Maint Status</th>
              <th>Departs In</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in notReadyDepartures" :key="'nr-' + row.name" @click="goToVsm">
              <td>{{ row.name }}</td>
              <td>{{ washLabel(row) }}</td>
              <td>{{ maintLabel(row) }}</td>
              <td><span class="dw-chip" :class="departsChip(row)">{{ departsLabel(row) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="dw-section-label" style="margin-top: 12px">Out of Service</div>
      <div v-if="!oosRows.length" class="dw-empty">None.</div>
      <template v-else>
        <div class="dw-stat-grid" style="margin-bottom: 10px">
          <div class="dw-stat-tile">
            <div class="dw-stat-tile-label">Out of Service</div>
            <div class="dw-stat-tile-value">{{ oosRows.length }}</div>
          </div>
          <div class="dw-stat-tile">
            <div class="dw-stat-tile-label">Past Due</div>
            <div class="dw-stat-tile-value">{{ items.length ? pastDueCount : '—' }}</div>
          </div>
        </div>
        <div class="dw-table-wrap">
          <table class="dw-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>OOS Date</th>
                <th>Days Past Due</th>
                <th>Open Issues</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topRows" :key="row.vehicle" @click="goToOosRow(row)">
                <td>{{ row.vehicle }}</td>
                <td>{{ row.fleetio ? formatDate(row.oos_date) : '—' }}</td>
                <td>{{ row.fleetio && row.days_past_due != null ? row.days_past_due : '—' }}</td>
                <td>{{ row.fleetio ? openIssueCount(row) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { get_vehicle_maint_summary, get_vehicle_info } from '../../networking/apis'
import { statusLabel } from '../../utils/vehicle_status_labels'
import { vsmBoardRouteName } from '../../utils/vsm_access'

// a coach fleet's departures cluster in the early morning, so a 4-hour
// window (the original) was empty for most of the working day on every
// tenant: check it at 4pm and the next departure is typically 12+ hours
// out, so the widget reported "all clear" precisely when someone still had
// time to act on a vehicle that isn't ready. 12 hours covers the next
// departure wave from any point in the day -- look in the afternoon and you
// see tomorrow morning's, look in the morning and you see the rest of today.
const DEPARTURE_WINDOW_HOURS = 12
const DEPARTURE_WINDOW_MS = DEPARTURE_WINDOW_HOURS * 60 * 60 * 1000
// inside this much of its departure, a not-ready vehicle is urgent rather
// than merely worth knowing about
const URGENT_WINDOW_MS = 4 * 60 * 60 * 1000
const MAX_NOT_READY_ROWS = 6
const MAX_OOS_ROWS = 5

// ready to depart = washed/cleaned AND maintenance-cleared; anything else
// on a vehicle leaving inside the window is worth a look
const READY_VEHICLE_STATUSES = ['CLEANED', 'CLEAN']
const READY_MAINT_STATUSES = ['READY']

export default {
  name: 'widget_vehicle_maintenance',
  props: {
    tenant: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      vehicles: [],
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // the heading states the window it is reporting on, read from the same
    // constant the filter uses, so the copy can't drift from the behavior
    departureWindowHours() {
      return DEPARTURE_WINDOW_HOURS
    },
    tenantType() {
      return (this.user.types && this.user.types[this.tenant]) || null
    },
    tenantFeatures() {
      return (this.user.features && this.user.features[this.tenant]) || {}
    },
    vsmRoute() {
      return vsmBoardRouteName(this.tenantType, this.tenantFeatures)
    },
    // the plain board carries OOS as a wash status; the maint, dvir and
    // fasttrak boards moved it to the maint column and dropped it from their
    // wash vocabulary. read the field the tenant's own board counts under its
    // OOS chip, so a stale value in the column that board doesn't show can't
    // put a vehicle on this list that the board calls in service.
    boardOosField() {
      const f = this.tenantFeatures
      if (this.tenantType === 'FASTTRAK') return 'maintenance_status'
      if (f.vehicle_status_management_maint_enabled || f.vehicle_status_management_dvir_enabled) return 'maintenance_status'
      return 'vehicle_status'
    },
    // the maint summary is Fleetio-only: a tenant without the integration gets
    // an empty list back, so on its own this section read "None." while the
    // board had buses marked OOS. Fleetio rows keep their detail and win a
    // name clash; a bus marked OOS only on the board is added bare, and its
    // Fleetio columns read as a dash rather than a zero it doesn't have.
    oosRows() {
      const rows = new Map()
      const key = name => String(name || '').trim().toUpperCase()
      this.items.forEach(row => {
        if (key(row.vehicle) && !rows.has(key(row.vehicle))) rows.set(key(row.vehicle), { ...row, fleetio: true })
      })
      this.vehicles
        .filter(v => String(v[this.boardOosField] || '').toUpperCase() === 'OOS')
        .map(v => String(v.name || '').trim())
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .forEach(name => {
          if (name && !rows.has(key(name))) rows.set(key(name), { vehicle: name, fleetio: false })
        })
      return [...rows.values()]
    },
    pastDueCount() {
      return this.items.filter(row => Number(row.days_past_due) > 0).length
    },
    // Fleetio rows first, most days past due first (only Fleetio work orders
    // carry a due date); the stable sort leaves board-only rows after them in
    // name order
    topRows() {
      return [...this.oosRows]
        .sort((a, b) => (Number(b.fleetio) - Number(a.fleetio)) ||
          (Number(b.days_past_due || 0) - Number(a.days_past_due || 0)))
        .slice(0, MAX_OOS_ROWS)
    },
    notReadyDepartures() {
      const cutoff = Date.now() + DEPARTURE_WINDOW_MS
      return this.vehicles
        .filter(v => {
          const nextMs = Number(v.next_mvmt_ms) * 1000
          if (!isFinite(nextMs) || nextMs <= 0 || nextMs > cutoff) return false
          const washOk = READY_VEHICLE_STATUSES.includes(String(v.vehicle_status || '').toUpperCase())
          const maintOk = READY_MAINT_STATUSES.includes(String(v.maintenance_status || '').toUpperCase())
          return !(washOk && maintOk)
        })
        .sort((a, b) => Number(a.next_mvmt_ms) - Number(b.next_mvmt_ms))
        .slice(0, MAX_NOT_READY_ROWS)
    },
  },
  created() {
    this.load()
  },
  methods: {
    washLabel(row) {
      return statusLabel(row.vehicle_status) || '—'
    },
    maintLabel(row) {
      return statusLabel(row.maintenance_status) || '—'
    },
    departsLabel(row) {
      if (row.next_mvmt_delta) {
        const delta = String(row.next_mvmt_delta)
        return delta.indexOf('late') === 0 ? delta : 'in ' + delta
      }
      return moment(Number(row.next_mvmt_ms) * 1000).fromNow()
    },
    // now that the window reaches 12 hours the rows are no longer uniformly
    // urgent, so the chip grades: red inside 4 hours (what the old window
    // covered), amber beyond it.
    departsChip(row) {
      const ms = Number(row.next_mvmt_ms) * 1000
      return (ms - Date.now()) <= URGENT_WINDOW_MS ? 'dw-chip-danger' : 'dw-chip-warning'
    },
    formatDate(value) {
      if (!value) return '—'
      const m = moment(String(value))
      return m.isValid() ? m.format('MM/DD') : String(value)
    },
    goToVsm() {
      this.$router.push({ name: this.vsmRoute }).catch(() => {})
    },
    // the summary only lists Fleetio's out-of-service vehicles, so a bus
    // marked OOS on the board alone would not be there; send it to the board
    goToOosRow(row) {
      if (!row.fleetio) return this.goToVsm()
      this.$router.push({ name: 'vehicle_maint_summary' }).catch(() => {})
    },
    // the summary sends issues as a list; the cell wants how many
    openIssueCount(row) {
      return Array.isArray(row.issues) ? row.issues.length : 0
    },
    async load() {
      this.loading = true
      this.loadError = false
      // maint summary and vehicle statuses fail independently: losing one
      // list shouldn't blank the other, so only a double failure shows the
      // widget-level error state.
      const [maint, vehicles] = await Promise.all([
        get_vehicle_maint_summary(this.tenant).catch(() => null),
        get_vehicle_info(this.tenant, '', '', false).catch(() => null),
      ])
      this.items = (maint && maint.value && maint.value.data && maint.value.data.results) || []
      this.vehicles = (vehicles && vehicles.value && vehicles.value.data && vehicles.value.data.results) || []
      this.loadError = !maint && !vehicles
      this.loading = false
    },
  },
}
</script>
